import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Cookie, X, Settings, Check } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'pastoria_cookie_consent';

const CookieConsent = () => {
  const { language, t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    thirdParty: false
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to prevent flash on page load
      const timer = setTimeout(() => setIsVisible(true), 500);
      return () => clearTimeout(timer);
    } else {
      try {
        const savedPreferences = JSON.parse(consent);
        // Use functional update to avoid lint warning
        setPreferences(prev => ({ ...prev, ...savedPreferences }));
      } catch (e) {
        setIsVisible(true);
      }
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setIsVisible(false);
    setShowPreferences(false);
    
    // Dispatch custom event for other components to react
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: prefs }));
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      thirdParty: true,
      timestamp: new Date().toISOString()
    });
  };

  const rejectAll = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      thirdParty: false,
      timestamp: new Date().toISOString()
    });
  };

  const savePreferences = () => {
    saveConsent({
      ...preferences,
      timestamp: new Date().toISOString()
    });
  };

  const translations = {
    es: {
      title: 'Utilizamos cookies',
      description: 'Este sitio web utiliza cookies para mejorar su experiencia. Las cookies necesarias son esenciales para el funcionamiento del sitio. Puede elegir aceptar o rechazar las cookies opcionales.',
      acceptAll: 'Aceptar todas',
      rejectAll: 'Rechazar todas',
      customize: 'Personalizar',
      savePreferences: 'Guardar preferencias',
      necessary: 'Cookies necesarias',
      necessaryDesc: 'Esenciales para el funcionamiento básico del sitio web. No se pueden desactivar.',
      analytics: 'Cookies analíticas',
      analyticsDesc: 'Nos ayudan a entender cómo los visitantes interactúan con el sitio web.',
      marketing: 'Cookies de marketing',
      marketingDesc: 'Se utilizan para mostrar anuncios relevantes y medir su efectividad.',
      thirdParty: 'Cookies de terceros',
      thirdPartyDesc: 'Cookies de servicios externos como Google Maps.',
      moreInfo: 'Más información en nuestra',
      cookiePolicy: 'Política de Cookies',
      close: 'Cerrar'
    },
    en: {
      title: 'We use cookies',
      description: 'This website uses cookies to improve your experience. Necessary cookies are essential for the site to function. You can choose to accept or reject optional cookies.',
      acceptAll: 'Accept all',
      rejectAll: 'Reject all',
      customize: 'Customize',
      savePreferences: 'Save preferences',
      necessary: 'Necessary cookies',
      necessaryDesc: 'Essential for basic website functionality. Cannot be disabled.',
      analytics: 'Analytics cookies',
      analyticsDesc: 'Help us understand how visitors interact with the website.',
      marketing: 'Marketing cookies',
      marketingDesc: 'Used to display relevant ads and measure their effectiveness.',
      thirdParty: 'Third-party cookies',
      thirdPartyDesc: 'Cookies from external services like Google Maps.',
      moreInfo: 'More information in our',
      cookiePolicy: 'Cookie Policy',
      close: 'Close'
    }
  };

  const text = translations[language] || translations.es;

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50" data-testid="cookie-consent-banner">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#356C8A]/10 rounded-full flex items-center justify-center">
                <Cookie className="h-5 w-5 text-[#356C8A]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">{text.title}</h2>
            </div>
            {showPreferences && (
              <button
                onClick={() => setShowPreferences(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label={text.close}
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {!showPreferences ? (
            <>
              <p className="text-gray-600 mb-4">{text.description}</p>
              <p className="text-sm text-gray-500 mb-6">
                {text.moreInfo}{' '}
                <Link
                  to={language === 'es' ? '/cookies' : '/en/cookies'}
                  className="text-[#356C8A] hover:underline font-medium"
                >
                  {text.cookiePolicy}
                </Link>
                .
              </p>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={acceptAll}
                  className="flex-1 px-6 py-3 bg-[#356C8A] text-white rounded-lg font-semibold hover:bg-[#2B5670] transition-colors"
                  data-testid="accept-all-cookies"
                >
                  {text.acceptAll}
                </button>
                <button
                  onClick={rejectAll}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                  data-testid="reject-all-cookies"
                >
                  {text.rejectAll}
                </button>
              </div>
              <button
                onClick={() => setShowPreferences(true)}
                className="w-full mt-3 px-6 py-3 text-[#356C8A] hover:bg-[#356C8A]/5 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                data-testid="customize-cookies"
              >
                <Settings className="h-4 w-4" />
                <span>{text.customize}</span>
              </button>
            </>
          ) : (
            <>
              {/* Cookie Preferences */}
              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">{text.necessary}</span>
                    <div className="flex items-center space-x-2">
                      <Check className="h-5 w-5 text-green-500" />
                      <span className="text-sm text-green-600 font-medium">
                        {language === 'es' ? 'Siempre activas' : 'Always active'}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">{text.necessaryDesc}</p>
                </div>

                {/* Analytics Cookies */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">{text.analytics}</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                        className="sr-only peer"
                        data-testid="analytics-toggle"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#356C8A]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#356C8A]"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">{text.analyticsDesc}</p>
                </div>

                {/* Marketing Cookies */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">{text.marketing}</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                        className="sr-only peer"
                        data-testid="marketing-toggle"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#356C8A]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#356C8A]"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">{text.marketingDesc}</p>
                </div>

                {/* Third-party Cookies */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">{text.thirdParty}</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.thirdParty}
                        onChange={(e) => setPreferences({ ...preferences, thirdParty: e.target.checked })}
                        className="sr-only peer"
                        data-testid="thirdparty-toggle"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#356C8A]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#356C8A]"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">{text.thirdPartyDesc}</p>
                </div>
              </div>

              <button
                onClick={savePreferences}
                className="w-full px-6 py-3 bg-[#356C8A] text-white rounded-lg font-semibold hover:bg-[#2B5670] transition-colors"
                data-testid="save-cookie-preferences"
              >
                {text.savePreferences}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Hook to check cookie consent
export const useCookieConsent = () => {
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    const checkConsent = () => {
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (stored) {
        try {
          setConsent(JSON.parse(stored));
        } catch (e) {
          setConsent(null);
        }
      }
    };

    checkConsent();

    const handleConsentChange = (e) => {
      setConsent(e.detail);
    };

    window.addEventListener('cookieConsentChanged', handleConsentChange);
    return () => window.removeEventListener('cookieConsentChanged', handleConsentChange);
  }, []);

  return consent;
};

export default CookieConsent;
